/**
 * Libellés des champs de comparaison — une seule source (chunk 9.AU.1).
 *
 * ## Pourquoi ce fichier a dû être créé
 *
 * Le libellé existait déjà, et le conseiller le voyait déjà : la matrice
 * comparative affiche « Code assureur » là où la donnée s'appelle `insurer_code`.
 * Mais ce libellé vivait dans `apps/web` — dictionnaire dans les fichiers i18n,
 * repli dans un module de page. Le worker, qui est dans un autre workspace, ne
 * pouvait pas l'atteindre.
 *
 * Conséquence concrète : un conseiller cliquait « demander » sur une ligne
 * intitulée « Code assureur », et l'assureur recevait un courriel disant
 * `insurer_code`. Le chemin technique traversait jusqu'au destinataire externe.
 *
 * Ce module est donc **le** vocabulaire, à côté du registre de champs qui vit
 * déjà ici depuis 9.AB.1 — même raison, même endroit : « consommé par l'API et
 * par le web, jamais réimplémenté côté frontend ».
 *
 * ## Ce que le repli fait, et ce qu'il ne fait pas
 *
 * Le dictionnaire couvre les champs des passes d'extraction déclarées et les
 * variantes historiques explicitement nommées. Une clé inconnue reste humanisée,
 * jamais traduite par supposition. Les valeurs et citations ne sont pas traduites.
 */

/** Les deux langues du produit. */
export type LabelLanguage = "fr" | "en";

/**
 * Libellés validés, par clé canonique.
 *
 * N'ajouter une entrée qu'avec un libellé **métier confirmé** — une traduction
 * approximative est pire que le repli, parce qu'elle a l'air d'être une décision.
 */
export const COMPARISON_FIELD_LABELS: Readonly<
  Record<string, Readonly<Record<LabelLanguage, string>>>
> = {
  // Familles du rapport : même vocabulaire partagé que leurs champs.
  amc: { fr: "Assurance maladie complémentaire", en: "Extended health care" },
  dentaire: { fr: "Soins dentaires", en: "Dental care" },
  dma: { fr: "Décès et mutilation accidentels", en: "Accidental death and dismemberment" },
  ic_court_terme: { fr: "Invalidité de courte durée", en: "Short-term disability" },
  ic_long_terme: { fr: "Invalidité de longue durée", en: "Long-term disability" },
  vie_base: { fr: "Vie de base", en: "Basic life" },
  effect_date: { fr: "Date d'effet", en: "Effective date" },
  prime_mensuelle_total: { fr: "Prime mensuelle totale", en: "Total monthly premium" },
  prime_annuelle_total: { fr: "Prime annuelle totale", en: "Total annual premium" },
  prime_par_garantie: { fr: "Primes par garantie", en: "Premiums by coverage" },
  prime_annuelle: {
    fr: "Prime annuelle — autre champ source",
    en: "Annual premium — additional source field",
  },
  prime_mensuelle: {
    fr: "Prime mensuelle — autre champ source",
    en: "Monthly premium — additional source field",
  },
  "vie_base.salary_multiple": {
    fr: "Vie de base — multiple du salaire",
    en: "Basic life — salary multiple",
  },
  "vie_base.max_amount": { fr: "Vie de base — montant maximal", en: "Basic life — maximum amount" },
  "vie_base.montant": { fr: "Vie de base — montant", en: "Basic life — amount" },
  "vie_base.reduction_age": {
    fr: "Vie de base — âge de réduction",
    en: "Basic life — reduction age",
  },
  "vie_base.reduction_percentage": {
    fr: "Vie de base — réduction (%)",
    en: "Basic life — reduction (%)",
  },
  "vie_base.elimination": {
    fr: "Vie de base — délai de carence",
    en: "Basic life — waiting period",
  },
  "dma.coverage": {
    fr: "Décès et mutilation accidentels — couverture",
    en: "Accidental death and dismemberment — coverage",
  },
  "dma.max_amount": {
    fr: "Décès et mutilation accidentels — montant maximal",
    en: "Accidental death and dismemberment — maximum amount",
  },
  "amc.deductible": { fr: "Franchise", en: "Deductible" },
  "amc.coinsurance": {
    fr: "Remboursement des frais médicaux (%)",
    en: "Health expense reimbursement (%)",
  },
  "amc.max_annual": {
    fr: "Maximum annuel des frais médicaux",
    en: "Annual health expense maximum",
  },
  "amc.medicaments_coinsurance": {
    fr: "Remboursement des médicaments (%)",
    en: "Prescription drug reimbursement (%)",
  },
  "amc.paramedical_max": { fr: "Maximum des soins paramédicaux", en: "Paramedical care maximum" },
  "amc.hospital_room": { fr: "Chambre d'hôpital", en: "Hospital room" },
  "amc.travel_max": { fr: "Maximum de la couverture voyage", en: "Travel coverage maximum" },
  "dentaire.preventif_coinsurance": {
    fr: "Soins dentaires préventifs — remboursement (%)",
    en: "Preventive dental care — reimbursement (%)",
  },
  "dentaire.basic_coinsurance": {
    fr: "Soins dentaires de base — remboursement (%)",
    en: "Basic dental care — reimbursement (%)",
  },
  "dentaire.major_coinsurance": {
    fr: "Soins dentaires majeurs — remboursement (%)",
    en: "Major dental care — reimbursement (%)",
  },
  "dentaire.ortho_coinsurance": {
    fr: "Orthodontie — remboursement (%)",
    en: "Orthodontics — reimbursement (%)",
  },
  "dentaire.max_annual": { fr: "Maximum annuel des soins dentaires", en: "Annual dental maximum" },
  "dentaire.fee_guide_year": {
    fr: "Année du guide des tarifs dentaires",
    en: "Dental fee guide year",
  },
  "ic_court_terme.percentage": {
    fr: "Invalidité de courte durée — salaire couvert (%)",
    en: "Short-term disability — salary coverage (%)",
  },
  "ic_court_terme.max_weekly": {
    fr: "Invalidité de courte durée — maximum hebdomadaire",
    en: "Short-term disability — weekly maximum",
  },
  "ic_court_terme.elimination": {
    fr: "Invalidité de courte durée — délai de carence",
    en: "Short-term disability — waiting period",
  },
  "ic_court_terme.duration": {
    fr: "Invalidité de courte durée — durée des prestations",
    en: "Short-term disability — benefit duration",
  },
  "ic_long_terme.percentage": {
    fr: "Invalidité de longue durée — salaire couvert (%)",
    en: "Long-term disability — salary coverage (%)",
  },
  "ic_long_terme.max_monthly": {
    fr: "Invalidité de longue durée — maximum mensuel",
    en: "Long-term disability — monthly maximum",
  },
  "ic_long_terme.elimination": {
    fr: "Invalidité de longue durée — délai de carence",
    en: "Long-term disability — waiting period",
  },
  "ic_long_terme.definition": {
    fr: "Invalidité de longue durée — définition",
    en: "Long-term disability — definition",
  },
  admissibility_hours: {
    fr: "Heures de travail requises pour l'admissibilité",
    en: "Work hours required for eligibility",
  },
  waiting_period_days: {
    fr: "Délai d'admissibilité (jours)",
    en: "Eligibility waiting period (days)",
  },
  exclusions: { fr: "Exclusions", en: "Exclusions" },
  general_notes: { fr: "Notes générales du document", en: "General document notes" },
  police: { fr: "Numéro de police", en: "Policy number" },
  preneur: { fr: "Preneur du contrat", en: "Policyholder" },
  date_document: { fr: "Date du document", en: "Document date" },
  date_effet: { fr: "Date d'effet", en: "Effective date" },
  document_type: { fr: "Type de document", en: "Document type" },
  entreprise: { fr: "Entreprise", en: "Company" },
  insurer_code: { fr: "Code assureur", en: "Insurer code" },
  is_blank_form: { fr: "Formulaire vierge", en: "Blank form" },
  language: { fr: "Langue", en: "Language" },
  numero_offre: { fr: "Numéro de l'offre", en: "Offer number" },
  prime_totale_mensuelle_base: {
    fr: "Prime totale mensuelle — base",
    en: "Total monthly premium — base",
  },
  prime_totale_mensuelle_ild: {
    fr: "Prime totale mensuelle — ILD",
    en: "Total monthly premium — ILD",
  },
  scenarios: { fr: "Scénarios", en: "Scenarios" },
  sections_present: { fr: "Sections présentes", en: "Sections present" },
  uncertainty: { fr: "Incertitude", en: "Uncertainty" },
};

/**
 * Rend une clé canonique lisible, sans la traduire.
 *
 * `coverage.dentaire_examen_rappel` → `Coverage › Dentaire examen rappel`.
 *
 * Casse de phrase par segment plutôt que Title Case : plus naturel en français,
 * et le séparateur `›` dit visuellement qu'il s'agit d'une hiérarchie de champ
 * sans employer un point, qui se lirait comme une fin de phrase.
 */
export function humanizeFieldKey(key: string): string {
  if (!key) return "";
  return key
    .split(".")
    .map((segment) => {
      const words = segment
        .split("_")
        .filter((w) => w.length > 0)
        .join(" ");
      return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : "";
    })
    .filter((s) => s.length > 0)
    .join(" › ");
}

/**
 * Le libellé à montrer à un humain : dictionnaire d'abord, repli ensuite.
 *
 * Ne renvoie **jamais** la clé brute — c'est tout l'objet du module. Une clé vide
 * rend une chaîne vide, à charge de l'appelant de ne pas en arriver là.
 */
export function comparisonFieldLabel(key: string, language: LabelLanguage): string {
  return COMPARISON_FIELD_LABELS[key]?.[language] ?? humanizeFieldKey(key);
}

/**
 * Les deux libellés d'un coup, pour les capturer au moment d'une demande.
 *
 * Une demande d'information est **capturée** puis expédiée plus tard, parfois
 * bien plus tard : le libellé est figé à la création, comme le titre et la
 * référence d'une demande de soumission. Si le dictionnaire s'enrichit entre
 * les deux, le courriel dira ce que le conseiller avait sous les yeux.
 */
export function comparisonFieldLabels(key: string): { fr: string; en: string } {
  return {
    fr: comparisonFieldLabel(key, "fr"),
    en: comparisonFieldLabel(key, "en"),
  };
}
